import { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import classnames from 'classnames'
import { SpinnerLoader } from 'shared/ui'
import { type IHubspotPropertiesObjectType, IntegrationKey } from 'entities/Integrations'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { EnumIntegrationTabs } from 'widgets/ContactsDetails'
import { Tabs } from 'widgets/ContactsDetails/ui/ContactsIntegrations/ui/shared/Tabs'
import { Properties } from 'widgets/ContactsDetails/ui/ContactsIntegrations/ui/shared/Entities/Properties'
import { Deals } from 'widgets/ContactsDetails/ui/ContactsIntegrations/ui/shared/Entities/Deals'
import { Companies } from 'widgets/ContactsDetails/ui/ContactsIntegrations/ui/shared/Entities/Companies'
import { Tickets } from 'widgets/ContactsDetails/ui/ContactsIntegrations/ui/shared/Entities/Tickets/Tickets'
import { useContactsIntegrationsContext } from 'widgets/ContactsDetails/ui/ContactsIntegrations/context/contactsIntegrationsContext'
import { ReconnectButton } from 'widgets/ContactsDetails/ui/ContactsIntegrations/ui/ReconnectButton/ReconnectButton'
import styles from './styles.module.scss'

export const HubspotIntegration = observer(() => {
  const store = useContactsIntegrationsContext()
  const { contactsStore, integrationsStore } = useEntitiesContext()
  const [activeTab, setActiveTab] = useState<EnumIntegrationTabs>(
    store.integrationTab || EnumIntegrationTabs.Contact
  )
  const contact = contactsStore.getItem(store.selectedContact?.id)
  const integration = integrationsStore.getIntegration(IntegrationKey.hubspot)

  const handleActiveTab = (tab: EnumIntegrationTabs) => {
    let objectType: IHubspotPropertiesObjectType = 'contact'
    setActiveTab(tab)
    store.handleIntegrationTab(tab)

    if (tab === EnumIntegrationTabs.Contact) objectType = 'contact'
    if (tab === EnumIntegrationTabs.Companies) objectType = 'company'
    if (tab === EnumIntegrationTabs.Deals) objectType = 'deal'
    if (tab === EnumIntegrationTabs.Tickets) objectType = 'ticket'

    store.setObjectType(objectType)
  }

  useEffect(() => {
    store.initDnD('hubspot', [
      {
        type: 'tickets',
        defaultItems: ['subject'],
      },
      {
        type: 'deals',
        defaultItems: ['dealname'],
      },
      {
        type: 'companies',
        defaultItems: ['name'],
      },
      {
        type: 'contacts',
        defaultItems: ['firstname', 'lastname'],
      },
    ])

    if (activeTab === EnumIntegrationTabs.Contact) {
      store.setObjectType('contact')
    }
  }, [])

  useEffect(() => {
    if (!contact) return
    if (!integration?.isConnected) return

    store.initContactIntegrationHubspotInfoByType(Number(contact.integration_vendor_id))
  }, [store.objectType])

  if (!contact) return null

  if (!integration?.isConnected) return <ReconnectButton redirectKey={IntegrationKey.hubspot} />

  const loading = store.loading || store.unlinking
  const info =
    integration.key === IntegrationKey.hubspot
      ? store.getContactIntegrationHubspotInfo(Number(contact.integration_vendor_id))
      : null

  if (loading) {
    return (
      <div className={classnames(styles.loaderWrap)}>
        <SpinnerLoader ariaLabel={'HubspotIntegration'} size={24} />
      </div>
    )
  }

  const ActiveTab = observer(() => {
    if (!info) return null

    if (activeTab === EnumIntegrationTabs.Deals) {
      return (
        <>
          <Deals
            integrationLabel={'HubSpot'}
            integrationKey={IntegrationKey.hubspot}
            integrationIcon={'hubspot'}
            contact={contact}
            deals={info.deals}
            error={info.dealsError}
            dnd={store.getDnDItem('hubspot', 'deals')}
          />
        </>
      )
    }

    if (activeTab === EnumIntegrationTabs.Companies) {
      return (
        <Companies
          integrationLabel={'HubSpot'}
          integrationKey={IntegrationKey.hubspot}
          integrationIcon={'hubspot'}
          contact={contact}
          companies={info.companies}
          error={info.companiesError}
          dnd={store.getDnDItem('hubspot', 'companies')}
        />
      )
    }

    if (activeTab === EnumIntegrationTabs.Contact) {
      return (
        <Properties
          integrationLabel={'HubSpot'}
          integrationKey={IntegrationKey.hubspot}
          property={info.properties || undefined}
          error={info.propertiesError}
          contact={contact}
          dnd={store.getDnDItem('hubspot', 'contacts')}
        />
      )
    }

    if (activeTab === EnumIntegrationTabs.Tickets) {
      return (
        <Tickets
          integrationLabel={'HubSpot'}
          integrationKey={IntegrationKey.hubspot}
          integrationIcon={'hubspot'}
          tickets={info.tickets}
          error={info.ticketsError}
          contact={contact}
          dnd={store.getDnDItem('hubspot', 'tickets')}
        />
      )
    }

    return null
  })

  return (
    <div className={styles.wrap}>
      <Tabs
        handleTab={handleActiveTab}
        activeTab={activeTab}
        items={[
          {
            key: EnumIntegrationTabs.Contact,
            title: 'Contact',
          },
          {
            key: EnumIntegrationTabs.Companies,
            title: 'Companies',
            enableCount: activeTab === EnumIntegrationTabs.Companies,
          },
          {
            key: EnumIntegrationTabs.Deals,
            title: 'Deals',
            enableCount: activeTab === EnumIntegrationTabs.Deals,
          },
          {
            key: EnumIntegrationTabs.Tickets,
            title: 'Tickets',
            enableCount: activeTab === EnumIntegrationTabs.Tickets,
          },
        ]}
      />
      <ActiveTab />
    </div>
  )
})
